import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes
} from 'react';
import { Link } from 'react-router-dom';
import loaderSvg from '@renderer/assets/icons/animated/loader.svg';
import { Icon, businessIconOptions, normalizeBusinessIconName, type BusinessIconName, type IconName } from './icons';

type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'info' | 'dark';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  label: string;
  active?: boolean;
}

export function IconButton({ icon, label, active = false, className, ...props }: IconButtonProps): JSX.Element {
  return (
    <button
      {...props}
      aria-label={label}
      className={['av-icon-button', active ? 'is-active' : '', className ?? ''].filter(Boolean).join(' ')}
      type={props.type ?? 'button'}
    >
      <Icon name={icon} />
    </button>
  );
}

interface PanelCardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  actions?: ReactNode;
}

export function PanelCard({ title, actions, className, children, ...props }: PanelCardProps): JSX.Element {
  return (
    <section {...props} className={['av-panel', className ?? ''].filter(Boolean).join(' ')}>
      {title || actions ? (
        <header className="av-panel__header">
          <div className="av-panel__title-wrap">{typeof title === 'string' ? <h2 className="av-panel__title">{title}</h2> : title}</div>
          {actions ? <div className="av-panel__actions">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

interface MetricCardProps {
  icon: IconName;
  label: string;
  value: string | number;
}

export function MetricCard({ icon, label, value }: MetricCardProps): JSX.Element {
  return (
    <PanelCard className="av-metric-card">
      <div className="av-metric-card__label">
        <Icon name={icon} />
        <span>{label}</span>
      </div>
      <strong className="av-metric-card__value">{value}</strong>
    </PanelCard>
  );
}

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function SectionHeader({ title, subtitle, actions }: SectionHeaderProps): JSX.Element {
  return (
    <div className="av-section-header">
      <div>
        <h1 className="av-section-header__title">{title}</h1>
        {subtitle ? <p className="av-section-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="av-section-header__actions">{actions}</div> : null}
    </div>
  );
}

interface StatusBadgeProps {
  label: string;
  tone: BadgeTone;
}

export function StatusBadge({ label, tone }: StatusBadgeProps): JSX.Element {
  return <span className={`av-status-badge av-status-badge--${tone}`}>{label}</span>;
}

interface ProgressBarProps {
  value: number;
  tone?: Exclude<BadgeTone, 'dark'>;
}

export function ProgressBar({ value, tone = 'success' }: ProgressBarProps): JSX.Element {
  return (
    <div className="av-progress" aria-label={`${value}%`}>
      <div className={`av-progress__fill av-progress__fill--${tone}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: 'small' | 'medium' | 'large';
  label?: string;
}

export function Spinner({ size = 'medium', label, className, ...props }: SpinnerProps): JSX.Element {
  const pixelSize = size === 'small' ? 18 : size === 'large' ? 40 : 24;
  return (
    <span
      {...props}
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
      className={['av-spinner', `av-spinner--${size}`, className ?? ''].filter(Boolean).join(' ')}
    >
      <img className="av-spinner__svg" src={loaderSvg} alt="" width={pixelSize} height={pixelSize} />
      {label ? <span className="av-spinner__label">{label}</span> : null}
    </span>
  );
}

interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon?: IconName;
  active?: boolean;
}

export function FilterChip({ label, icon, active = false, className, ...props }: FilterChipProps): JSX.Element {
  return (
    <button
      {...props}
      className={['av-filter-chip', active ? 'is-active' : '', className ?? ''].filter(Boolean).join(' ')}
      type={props.type ?? 'button'}
    >
      {icon ? <Icon name={icon} /> : null}
      <span>{label}</span>
    </button>
  );
}

interface TabOption<T extends string> {
  id: T;
  label: string;
  icon?: IconName;
}

interface EditorTabsProps<T extends string> {
  tabs: Array<TabOption<T>>;
  value: T;
  onChange: (tab: T) => void;
}

export function EditorTabs<T extends string>({ tabs, value, onChange }: EditorTabsProps<T>): JSX.Element {
  return (
    <div className="av-editor-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          aria-selected={tab.id === value}
          className={['av-editor-tabs__tab', tab.id === value ? 'is-active' : ''].filter(Boolean).join(' ')}
          role="tab"
          type="button"
          onClick={() => onChange(tab.id)}
        >
          {tab.icon ? <Icon name={tab.icon} /> : null}
          <span>{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

export function ToolbarGroup({ children, className }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={['av-toolbar-group', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  unstyled?: boolean;
  value: string;
  onValueChange?: (value: string) => void;
}

function useResponsiveFieldValue(value: string, onValueChange?: (value: string) => void): [string, (value: string) => void] {
  const [localValue, setLocalValue] = useState(value);
  const [, startTransition] = useTransition();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  function setResponsiveValue(nextValue: string): void {
    setLocalValue(nextValue);
    startTransition(() => {
      onValueChange?.(nextValue);
    });
  }

  return [localValue, setResponsiveValue];
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { value, onValueChange, className, onChange, type = 'text', unstyled = false, ...props },
  ref
): JSX.Element {
  const [localValue, setLocalValue] = useResponsiveFieldValue(value, onValueChange);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    setLocalValue(event.target.value);
    onChange?.(event);
  }

  return (
    <input
      {...props}
      ref={ref}
      className={[unstyled ? '' : 'av-input', className ?? ''].filter(Boolean).join(' ')}
      type={type}
      value={localValue}
      onChange={handleChange}
    />
  );
});

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onValueChange?: (value: string) => void;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField(
  { value, onValueChange, className, onChange, ...props },
  ref
): JSX.Element {
  const [localValue, setLocalValue] = useResponsiveFieldValue(value, onValueChange);

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setLocalValue(event.target.value);
    onChange?.(event);
  }

  return <textarea {...props} ref={ref} className={['av-input', className ?? ''].filter(Boolean).join(' ')} value={localValue} onChange={handleChange} />;
});

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: Array<{ value: string; label: string }>;
}

export function SelectField({ options, className, ...props }: SelectFieldProps): JSX.Element {
  return (
    <select {...props} className={['av-select', className ?? ''].filter(Boolean).join(' ')}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export interface SearchableSelectOption {
  value: string;
  label: string;
  icon?: IconName | undefined;
  description?: string | undefined;
  disabled?: boolean | undefined;
}

interface SearchableSelectFieldProps {
  options: SearchableSelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string | undefined;
  emptyLabel: string;
  clearLabel?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

function normalizeSearchText(value: string): string {
  return value.trim().toLocaleLowerCase();
}

export function SearchableSelectField({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  clearLabel,
  disabled = false,
  className
}: SearchableSelectFieldProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectedOption = options.find((option) => option.value === value) ?? null;
  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeSearchText(query);

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      normalizeSearchText(`${option.label} ${option.description ?? ''} ${option.value}`).includes(normalizedQuery)
    );
  }, [options, query]);
  const enabledOptions = filteredOptions.filter((option) => !option.disabled);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(0);
      return;
    }

    setActiveIndex(0);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  function selectOption(option: SearchableSelectOption): void {
    if (option.disabled) {
      return;
    }

    onValueChange(option.value);
    setOpen(false);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(enabledOptions.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = enabledOptions[activeIndex];

      if (option) {
        selectOption(option);
      }
    }
  }

  return (
    <div
      className={['av-searchable-select', open ? 'is-open' : '', className ?? ''].filter(Boolean).join(' ')}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <button
        aria-expanded={open}
        className="av-searchable-select__trigger"
        disabled={disabled}
        type="button"
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="av-searchable-select__value">
          {selectedOption?.icon ? <Icon name={selectedOption.icon} /> : null}
          <span>{selectedOption?.label ?? placeholder}</span>
        </span>
      </button>
      {value && clearLabel ? (
        <button
          aria-label={clearLabel}
          className="av-searchable-select__clear"
          disabled={disabled}
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onValueChange('');
            setOpen(false);
          }}
        >
          <Icon name="times" />
        </button>
      ) : null}
      {open ? (
        <div className="av-searchable-select__popover">
          <input
            ref={inputRef}
            className="av-searchable-select__search"
            placeholder={searchPlaceholder ?? placeholder}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="av-searchable-select__options" role="listbox">
            {filteredOptions.length === 0 ? (
              <div className="av-searchable-select__empty">{emptyLabel}</div>
            ) : (
              filteredOptions.map((option) => {
                const activeEnabledIndex = enabledOptions.findIndex((enabledOption) => enabledOption.value === option.value);
                const selected = option.value === value;
                const active = activeEnabledIndex === activeIndex;

                return (
                  <button
                    key={option.value}
                    aria-selected={selected}
                    className={[
                      'av-searchable-select__option',
                      selected ? 'is-selected' : '',
                      active ? 'is-active' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={option.disabled}
                    role="option"
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectOption(option)}
                  >
                    {option.icon ? <Icon name={option.icon} /> : null}
                    <span className="av-searchable-select__option-copy">
                      <strong>{option.label}</strong>
                      {option.description ? <span>{option.description}</span> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface IconPickerFieldProps {
  value: string;
  onValueChange: (value: BusinessIconName) => void;
  labelForIcon: (labelKey: string) => string;
  placeholder: string;
  searchPlaceholder?: string | undefined;
  emptyLabel: string;
  clearLabel?: string | undefined;
  disabled?: boolean | undefined;
}

export function IconPickerField({
  value,
  onValueChange,
  labelForIcon,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  clearLabel,
  disabled
}: IconPickerFieldProps): JSX.Element {
  const options = businessIconOptions.map((option) => ({
    value: option.value,
    label: labelForIcon(option.labelKey),
    icon: option.value
  }));

  return (
    <SearchableSelectField
      disabled={disabled}
      emptyLabel={emptyLabel}
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      value={normalizeBusinessIconName(value)}
      clearLabel={clearLabel}
      onValueChange={(nextValue) => onValueChange(normalizeBusinessIconName(nextValue))}
    />
  );
}

interface EmptyStateProps {
  title: string;
  description: string;
}

export function EmptyState({ title, description }: EmptyStateProps): JSX.Element {
  return (
    <div className="av-empty-state">
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

export function Breadcrumbs({ items }: BreadcrumbsProps): JSX.Element {
  return (
    <nav aria-label="Breadcrumb" className="av-breadcrumbs">
      {items.map((item, index) => (
        <span key={`${item.label}-${index}`} className="av-breadcrumbs__item">
          {index > 0 ? <span className="av-breadcrumbs__separator">/</span> : null}
          {item.to ? (
            <Link className="av-breadcrumbs__link" to={item.to}>
              {item.label}
            </Link>
          ) : (
            <span>{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function SearchTrigger({
  label,
  shortcut,
  onClick
}: {
  label: string;
  shortcut: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}): JSX.Element {
  return (
    <button className="av-search-trigger" type="button" onClick={onClick}>
      <span className="av-search-trigger__leading">
        <Icon name="search" />
        <span>{label}</span>
      </span>
      <span className="av-search-trigger__shortcut">{shortcut}</span>
    </button>
  );
}

export function OverflowMenuButton({
  label = 'More',
  className,
  onClick
}: {
  label?: string;
  className?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}): JSX.Element {
  return (
    <button aria-label={label} className={['av-panel-menu-button', className ?? ''].filter(Boolean).join(' ')} type="button" onClick={onClick}>
      <span aria-hidden>⋯</span>
    </button>
  );
}

export function PanelMenuButton(props: {
  label?: string;
  className?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}): JSX.Element {
  return <OverflowMenuButton {...props} />;
}

export function ValuePair({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="av-value-pair">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
